// apps/client/src/pages/MissionsPage.tsx
//
// Missions & Rewards — V2 Beelucky Sweepstakes
// Features: Auto-sort (completed first), optimistic claim, English copy, shared Toast
//

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useUserStore } from '../store/userStore';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui-kit/Button';
import { GlassCard } from '../ui-kit/GlassCard';
import { BottomNav } from '../ui-kit/Layouts';
import Icon from '../ui-kit/Icon';
import { ReferralDashboard } from '../ui-kit/ReferralDashboard';
import { MissionItem, MissionData } from '../ui-kit/MissionItem';
import { Toast, useToast } from '../ui-kit/Toast';
import { AdsgramTask } from '../components/AdsgramTask';

type TabType = 'daily' | 'extra';

interface MissionsPageProps {
    onNavigate: (view: 'home' | 'game' | 'missions' | 'store' | 'referrals' | 'sunday' | 'profile') => void;
}

export function MissionsPage({ onNavigate }: MissionsPageProps) {
    const {
        missions,
        missionsLoading,
        chips,
        referralCode,
        referralShareUrl,
        referralStats,
        fetchMissions,
        fetchReferralData,
        claimReward,
    } = useUserStore();
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState<TabType>('daily');
    const { toast, showToast, dismissToast } = useToast();

    useEffect(() => {
        fetchMissions();
        fetchReferralData();
    }, [fetchMissions, fetchReferralData]);



    // Group missions
    // Sort: COMPLETED first (ready to claim), then IN_PROGRESS, then CLAIMED
    const sortOrder = { COMPLETED: 0, IN_PROGRESS: 1, CLAIMED: 2 } as Record<string, number>;
    const dailyMissions = missions.filter(m => m.type === 'DAILY').sort((a, b) => (sortOrder[a.status] ?? 1) - (sortOrder[b.status] ?? 1)) as MissionData[];
    const extraMissions = missions.filter(m => m.type !== 'DAILY').sort((a, b) => (sortOrder[a.status] ?? 1) - (sortOrder[b.status] ?? 1)) as MissionData[];
    const currentMissions = activeTab === 'daily' ? dailyMissions : extraMissions;

    const handleClaim = async (missionId: string) => {
        const result = await claimReward(missionId);
        if (result.success && result.reward) {
            showToast(t('missions.earnedChips', { reward: result.reward }), 'success');
        }
    };

    const handleTaskReward = async () => {
        try {
            const { api } = await import('../lib/api');
            const result = await api.claimAdReward('sponsor_task');
            if (result.ok) {
                showToast(t('missions.earnedChipsTask', { reward: result.reward }), 'success');
            } else {
                showToast(t('missions.taskCompleted'), 'success');
            }
        } catch {
            showToast(t('missions.taskCompleted'), 'success');
        }
        fetchMissions();
    };

    const handleNavAction = (tab: string) => {
        const validViews = ['game', 'store', 'sunday', 'home', 'missions', 'referrals', 'profile'] as const;
        type ValidView = typeof validViews[number];
        if (tab === 'missions') return; // already here
        if (validViews.includes(tab as ValidView)) {
            onNavigate(tab as ValidView);
        } else {
            onNavigate('home');
        }
    };

    const tabs: { key: TabType; label: string }[] = [
        { key: 'daily', label: t('missions.tabDaily') },
        { key: 'extra', label: t('missions.tabExtra') },
    ];

    return (
        <div className="min-h-screen bg-purple-gradient flex flex-col relative overflow-hidden">
            {/* Background gradient */}
            <div className="fixed inset-0 z-0 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-surface/50 to-transparent" />
                <div className="absolute top-[-100px] right-[-100px] w-[300px] h-[300px] bg-primary/10 rounded-full blur-[100px]" />
            </div>

            {/* Header */}
            <header className="relative z-10 px-4 pt-6 pb-4 flex items-center justify-between">
                <div>
                    <span className="text-[10px] font-bold text-primary uppercase tracking-[0.3em]">{t('home.bingoton')}</span>
                    <h1 className="text-3xl font-display font-black gradient-text-gold">{t('missions.title')}</h1>
                    <p className="text-white/60 text-sm font-medium">{t('missions.subtitle')}</p>
                </div>
                <div className="bg-gold-gradient px-4 py-2 rounded-full flex items-center gap-2 shadow-glow-gold">
                    <Icon name="toll" className="text-lg text-[#1a0b2e]" />
                    <span className="font-black text-background">{chips.toLocaleString()}</span>
                </div>
            </header>

            <main className="flex-1 px-4 pb-32 space-y-6 relative z-10">
                {/* Referral Dashboard */}
                <ReferralDashboard
                    code={referralCode}
                    shareUrl={referralShareUrl}
                    count={referralStats?.totalReferrals ?? 0}
                    earnings={referralStats?.totalEarned ?? 0}
                />

                {/* Task Ad — Adsgram native web component */}
                <GlassCard className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-surface border border-white/10 flex items-center justify-center">
                            <Icon name="task_alt" className="text-primary" />
                        </div>
                        <div>
                            <h3 className="font-bold text-white leading-tight text-sm">{t('missions.sponsoredTask')}</h3>
                            <p className="text-xs text-white/50">{t('missions.completeToEarn')}</p>
                        </div>
                    </div>
                    <AdsgramTask
                        onReward={handleTaskReward}
                        onError={() => showToast(t('missions.taskError'), 'error')}
                        onBannerNotFound={() => showToast(t('missions.noTasks'), 'info')}
                        className="w-full"
                    />
                </GlassCard>

                {/* Tabs */}
                <div className="p-1 glass-panel-deep rounded-xl flex gap-1 border border-white/5">
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`
                                flex-1 py-2.5 rounded-lg font-bold text-sm transition-all duration-300 relative overflow-hidden
                                ${activeTab === tab.key
                                    ? 'text-background shadow-lg'
                                    : 'text-white/60 hover:text-white hover:bg-white/5'
                                }
                            `}
                        >
                            {activeTab === tab.key && (
                                <motion.div
                                    layoutId="activeTab"
                                    className="absolute inset-0 bg-gold-gradient"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-10">{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Loading */}
                {missionsLoading && (
                    <div className="text-center py-8">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            className="inline-block text-4xl mb-4"
                        >
                            🎯
                        </motion.div>
                        <p className="text-white/40">{t('missions.loading')}</p>
                    </div>
                )}

                {/* Mission List */}
                <div className="space-y-3">
                    {currentMissions.map((mission, idx) => (
                        <motion.div
                            key={mission.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                        >
                            <MissionItem
                                mission={mission}
                                onClaim={handleClaim}
                                index={idx}
                            />
                        </motion.div>
                    ))}
                </div>

                {/* Empty state */}
                {!missionsLoading && currentMissions.length === 0 && (
                    <GlassCard variant="strong" className="p-8 text-center">
                        <span className="text-5xl mb-4 block">📋</span>
                        <p className="text-white font-bold mb-2">{t('missions.noActive')}</p>
                        <p className="text-sm text-white/40">{t('missions.checkBack')}</p>
                    </GlassCard>
                )}

                {/* Refresh */}
                <Button
                    onClick={() => fetchMissions()}
                    variant="ghost"
                    fullWidth
                    icon="refresh"
                    disabled={missionsLoading}
                >
                    {t('missions.refresh')}
                </Button>
            </main>

            {/* Toast */}
            <Toast toast={toast} onDismiss={dismissToast} />

            <BottomNav activeTab="missions" onTabChange={handleNavAction} />
        </div>
    );
}
